import { useEffect, useMemo, useState } from 'react';
import { Loader, Radio, TriangleAlert } from 'lucide-react';
import { ESTADOS_DIARIOS_UNIDAD, type EstadoDiarioUnidad, type FuenteTelemetriaNombre, type MicrotrackEstado } from '@tms/contratos';
import { api } from '../api';
import { ChipEstado } from '../components/ChipEstado';
import { SelectorFecha } from '../components/SelectorFecha';
import { DIARIO } from '../components/estados';
import { fmtDiaCorto, fmtFechaHora, fmtHace, fmtHorasDecimal, fmtNum, hoyAR } from '../util/format';
import { useEstadoDiarioSemana } from '../util/estadoDiario';
import { useCarga } from '../util/hooks';

const UMBRAL_SIN_REPORTE_MIN = 15;
const FUENTES: FuenteTelemetriaNombre[] = ['webhook', 'polling'];

function PanelMicrotrack({ m }: { m: MicrotrackEstado }) {
  const sinReportar = m.vehiculos.filter((v) => v.enViaje && v.minutosSinReportar > UMBRAL_SIN_REPORTE_MIN);
  return (
    <>
      <div className="body">
        <dl className="kv">
          <dt>Modo de recepción</dt>
          <dd>{m.modo}</dd>
          <dt>API de Microtrack</dt>
          <dd>{m.apiConfigurada ? 'Configurada' : 'Sin configurar'}</dd>
          <dt>Cola de ingesta</dt>
          <dd>
            {m.cola.esperando} esperando · {m.cola.activos} activos · {m.cola.fallidos} fallidos
          </dd>
        </dl>

        <div className="sub-h">Eventos por fuente</div>
        <table className="mt-tabla">
          <thead>
            <tr>
              <th>Fuente</th>
              <th>Recibidos</th>
              <th>Duplicados</th>
              <th>Descartados</th>
            </tr>
          </thead>
          <tbody>
            {FUENTES.map((f) => (
              <tr key={f}>
                <td>{f === 'webhook' ? 'Webhook' : 'Polling'}<br /><span className="muted">último {fmtHace(m.porFuente[f].ultimoRecibidoEn)}</span></td>
                <td>{fmtNum(m.porFuente[f].recibidos)}</td>
                <td>{fmtNum(m.porFuente[f].duplicados)}</td>
                <td>{fmtNum(m.porFuente[f].descartados)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="sub-h">Latencia (recepción menos hora del evento)</div>
        <dl className="kv">
          <dt>Promedio</dt>
          <dd>{m.latencia.promedioSeg === null ? '—' : `${fmtNum(m.latencia.promedioSeg, 1)} s`}</dd>
          <dt>Percentil 95</dt>
          <dd>{m.latencia.p95Seg === null ? '—' : `${fmtNum(m.latencia.p95Seg, 1)} s`}</dd>
          <dt>Máxima</dt>
          <dd>{m.latencia.maximaSeg === null ? '—' : `${fmtNum(m.latencia.maximaSeg, 1)} s`}</dd>
        </dl>

        <div className="sub-h">Vehículos sin reportar (más de {UMBRAL_SIN_REPORTE_MIN} min, en viaje)</div>
        {sinReportar.length === 0 ? (
          <p className="muted" style={{ fontSize: 12 }}>Todos los vehículos en viaje están reportando.</p>
        ) : (
          <dl className="kv">
            {sinReportar.map((v) => (
              <div key={v.vehiculoIdMicrotrack} style={{ display: 'contents' }}>
                <dt>{v.vehiculoNombre ?? v.vehiculoIdMicrotrack}</dt>
                <dd className="red">{v.minutosSinReportar} min</dd>
              </div>
            ))}
          </dl>
        )}

        <div className="sub-h">Huecos recuperados por polling</div>
        <dl className="kv">
          <dt>Recuperados</dt>
          <dd>{m.huecos.recuperados}</dd>
          <dt>Fallidos</dt>
          <dd className={m.huecos.fallidos > 0 ? 'amber' : ''}>{m.huecos.fallidos}</dd>
        </dl>
        {m.huecos.ultimos.length > 0 && (
          <table className="mt-tabla" style={{ marginTop: 10 }}>
            <thead>
              <tr>
                <th>Vehículo</th>
                <th>Período</th>
                <th>Nuevos</th>
              </tr>
            </thead>
            <tbody>
              {m.huecos.ultimos.map((h, i) => (
                <tr key={i}>
                  <td>{h.vehiculoIdMicrotrack}</td>
                  <td>{fmtFechaHora(h.desde)}</td>
                  <td className={h.error ? 'amber' : ''} title={h.error ?? undefined}>
                    {h.error ? 'Con error' : `${h.eventosNuevos}/${h.eventosObtenidos}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {m.vehiculosDesconocidos.length > 0 && <p className="muted" style={{ fontSize: 11, marginTop: 12 }}>Vehículos desconocidos: {m.vehiculosDesconocidos.join(', ')}</p>}
      </div>
    </>
  );
}

export function FlotaPagina() {
  const [fecha, setFecha] = useState(hoyAR());
  const dia = useCarga(() => api.estadoDiario(fecha), [fecha]);
  const semana = useEstadoDiarioSemana(hoyAR());
  const mt = useCarga(() => api.microtrackEstado(), []);

  // La telemetría se refresca sola cada 30 s.
  const { recargar: recargarMt } = mt;
  useEffect(() => {
    const t = setInterval(recargarMt, 30_000);
    return () => clearInterval(t);
  }, [recargarMt]);

  const unidades = useMemo(() => [...(dia.datos?.unidades ?? [])].sort((a, b) => (a.patente ?? '').localeCompare(b.patente ?? '')), [dia.datos]);
  const cuentas = useMemo(() => {
    const c: Record<EstadoDiarioUnidad, number> = { EN_VIAJE: 0, ACTIVA_SIN_VIAJE: 0, SIN_ACTIVIDAD: 0 };
    for (const u of unidades) c[u.estado]++;
    return c;
  }, [unidades]);

  // Grilla de 7 días: unidad -> día -> estado
  const grilla = useMemo(() => {
    const porUnidad = new Map<string, { patente: string; dias: Map<string, EstadoDiarioUnidad> }>();
    for (const u of semana.datos?.unidades ?? []) {
      const e = porUnidad.get(u.vehiculoIdMicrotrack) ?? { patente: u.patente ?? u.vehiculoIdMicrotrack, dias: new Map() };
      e.dias.set(u.fecha, u.estado);
      porUnidad.set(u.vehiculoIdMicrotrack, e);
    }
    return [...porUnidad.entries()].sort((a, b) => a[1].patente.localeCompare(b[1].patente));
  }, [semana.datos]);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">OPERACIONES / UNIDADES</div>
          <h1>Flota</h1>
        </div>
        <SelectorFecha fecha={fecha} onCambiar={setFecha} />
      </div>

      {(dia.error || semana.error) && (
        <div className="banner error" role="alert">
          <TriangleAlert aria-hidden />
          {dia.error ?? semana.error}
        </div>
      )}

      <div className="grid-flota">
        <div>
          <section className="card" aria-label="Estado diario de las unidades">
            <header>
              <h2>Estado diario</h2>
              <span className="list-count">{unidades.length} unidades</span>
            </header>
            <div className="resumen-dia">
              {ESTADOS_DIARIOS_UNIDAD.map((e) => (
                <span key={e} className={`chip ${DIARIO[e].color}`}>
                  {(() => {
                    const I = DIARIO[e].icono;
                    return <I aria-hidden />;
                  })()}
                  {DIARIO[e].etiqueta}: {cuentas[e]}
                </span>
              ))}
            </div>
            {dia.cargando && !dia.datos ? (
              <div className="skeleton"><Loader className="spin" aria-hidden size={16} />Cargando estado diario…</div>
            ) : (
              <div className="table-wrap" style={{ maxHeight: 420 }}>
                <table className="tabla">
                  <thead>
                    <tr>
                      <th>Unidad</th>
                      <th>Estado</th>
                      <th className="num">Horas en viaje</th>
                      <th className="num">km</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unidades.map((u) => (
                      <tr key={u.vehiculoIdMicrotrack}>
                        <td><strong>{u.patente ?? u.vehiculoIdMicrotrack}</strong></td>
                        <td><ChipEstado tipo="diario" valor={u.estado} /></td>
                        <td className="num">{u.horasEnViaje > 0 ? fmtHorasDecimal(u.horasEnViaje) : '—'}</td>
                        <td className="num">{u.km > 0 ? fmtNum(u.km, 1) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="card" aria-label="Últimos 7 días">
            <header>
              <h2>Últimos 7 días</h2>
            </header>
            <div className="body table-wrap">
              {semana.cargando && !semana.datos ? (
                <div className="skeleton"><Loader className="spin" aria-hidden size={16} />Cargando…</div>
              ) : (
                <table className="semana">
                  <thead>
                    <tr>
                      <th className="u">Unidad</th>
                      {semana.dias.map((d) => {
                        const f = fmtDiaCorto(d);
                        return (
                          <th key={d}>
                            {f.semana}
                            <br />
                            {f.fecha}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {grilla.map(([id, u]) => (
                      <tr key={id}>
                        <td className="u">{u.patente}</td>
                        {semana.dias.map((d) => {
                          const e = u.dias.get(d);
                          if (!e) return <td key={d}><span className="celda muted" title="Sin dato">—</span></td>;
                          const p = DIARIO[e];
                          return (
                            <td key={d}>
                              <span className={`celda ${p.color}`} title={`${u.patente} · ${fmtDiaCorto(d).fecha} · ${p.etiqueta}`}>
                                <p.icono aria-label={p.etiqueta} />
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="leyenda">
              {ESTADOS_DIARIOS_UNIDAD.map((e) => (
                <span key={e} className={DIARIO[e].color === 'brand' ? 'green' : DIARIO[e].color}>
                  {(() => {
                    const I = DIARIO[e].icono;
                    return <I aria-hidden size={13} />;
                  })()}
                  {DIARIO[e].etiqueta}
                </span>
              ))}
            </div>
          </section>
        </div>

        <section className="card" aria-label="Telemetría Microtrack">
          <header>
            <h2>
              <Radio aria-hidden size={16} />
              Telemetría Microtrack
            </h2>
          </header>
          {mt.error && !mt.datos && <div className="banner error" style={{ margin: 16 }}><TriangleAlert aria-hidden />{mt.error}</div>}
          {mt.cargando && !mt.datos && <div className="skeleton"><Loader className="spin" aria-hidden size={16} />Cargando telemetría…</div>}
          {mt.datos && <PanelMicrotrack m={mt.datos} />}
        </section>
      </div>
    </div>
  );
}
